import { SITE_ORIGIN, HT } from "../siteConfig";
import  SEO from "../components/seo.jsx"
import { useRef, useEffect, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// ── Swap these asset imports per page ──
import HeroImg       from "../assets/fue-breadcrumb-bg.png";
import BreadcrumbImg from "../assets/fue-breadcrumb-bg.jpg";
import SurgeonImg    from "../assets/drbhaveshgupta.jpeg";
import ProcedureImg  from "../assets/fue-procedure.jpg";
import ResultImg     from "../assets/fue-result.jpg";

gsap.registerPlugin(ScrollTrigger);
const cinzel = { fontFamily: "'Cinzel', serif" };

/* ============================================================
   PAGE DATA
============================================================ */
const PAGE = {
  hero: {
    eyebrow: "Hair Restoration",
    heading: "FUE Hair Transplant",
    sub: "The most advanced, minimally invasive hair transplant technique for natural, permanent results at Clinique Internationale.",
  },

  breadcrumb: [
    { label: "Home", href: "/" },
    { label: "Treatments", href: "/follicular-unit-extraction.php" },
    { label: "FUE Hair Transplant", href: "/follicular-unit-extraction.php" },
  ],

  /* ── INTRO ── */
  intro: {
    eyebrow: "Overview",
    heading: "What is FUE Hair Transplant?",
    paragraphs: [
      "Follicular Unit Extraction (FUE) is a revolutionary hair transplant technique that has transformed the field of hair restoration. Unlike traditional methods, FUE offers a minimally invasive procedure with remarkable natural-looking results.",
      "By individually extracting hair follicles from the donor area and implanting them into the recipient area, FUE ensures a seamless and virtually undetectable hairline at Clinique Internationale, we understand the impact of hair loss on an individual's self-esteem and overall well-being, and FUE remains our leading solution for lasting hair restoration.",
    ],
  },

  /* ── THE FUE ADVANTAGE ── */
  science: {
    eyebrow: "Why FUE",
    heading: "The FUE Advantage",
    intro: "FUE stands apart from older transplant methods through the precision of individual follicle extraction and the natural way each graft is placed.",
    growthFactors: [
      { code: "PRE", name: "Precision Extraction",  desc: "Each follicle is individually extracted using a small, specialised punch tool, ensuring minimal scarring and a natural-looking result." },
      { code: "DHT", name: "DHT Resistance",         desc: "Transplanted follicles are resistant to DHT, the hormone responsible for pattern baldness, so growth continues long after the procedure." },
      { code: "NAT", name: "Natural Direction",      desc: "Follicles are implanted to match the natural angle and direction of surrounding hair, for a seamless, undetectable blend." },
      { code: "VER", name: "Versatility",            desc: "Suitable for both men and women whether restoring a receding hairline, filling thinning areas, or enhancing overall density." },
    ],
    mechanisms: [
      { num: "01", title: "Minimally Invasive",   desc: "No linear scars or visible signs of surgery, unlike traditional strip-harvesting methods." },
      { num: "02", title: "Faster Recovery",      desc: "Minimal downtime means most patients return to normal activities within days." },
      { num: "03", title: "Seamless Hairline",    desc: "Precise extraction and implantation create a hairline that blends naturally with existing hair." },
      { num: "04", title: "Lasting Results",      desc: "Natural, permanent growth that continues to thicken and mature over the following months." },
    ],
  },

  /* ── SURGEON ── */
  surgeon: {
    eyebrow: "Expertise and Experience",
    heading: "FUE Hair Transplant at Clinique Internationale",
    paragraphs: [
      "At Clinique Internationale, we take pride in our team of highly skilled and experienced hair transplant specialists led by Dr. Bhavesh Gupta. Our surgeons have undergone rigorous training and possess in-depth knowledge of the latest techniques in hair restoration.",
      "With a track record of successful procedures and countless satisfied clients, our team ensures personalised care and attention to detail at every stage of your FUE journey.",
    ],
    tags: [ "Advanced Fellowship – Cosmetic Surgery", "AOSMI Member", "15+ Yrs Experience", "10,000+ Cases"],
  },

  /* ── COST ── */
  cost: {
    eyebrow: "Pricing",
    heading: `FUE Hair Transplant Cost in ${HT.placeName}`,
    paragraphs: [
      "The average cost of FUE hair transplant at Clinique Internationale ranges from ₹50,000 to ₹1,50,000. The exact cost may vary depending on individual factors such as the extent of hair loss, the number of grafts required, and any additional procedures or treatments.",
    ],
    cities: [
      { city: "Mumbai",    range: "₹50,000 – ₹1,50,000", href: "/",      surgeon: "Dr. Bhavesh Gupta"  },
      { city: "Bangalore", range: "₹45,000 – ₹1,40,000", href: "https://bangalore.hairclinique.in/",              surgeon: "Dr. Idris MD"          },
      { city: "Pune",      range: "₹45,000 – ₹1,40,000", href: "https://pune.hairclinique.in/", surgeon: "Dr. Ajinkya Patil"  },
      { city: "Shirdi",    range: "₹45,000 – ₹1,40,000", href: "https://shirdi.hairclinique.in/",                    surgeon: "Dr. Pranay Thakur"    },
    ],
  },

  /* ── PROCEDURE STEPS ── */
  procedure: {
    eyebrow: "The Procedure",
    heading: "How FUE Hair Transplant is Performed",
    intro: "Understanding the procedure is crucial to making an informed decision here is what to expect at every stage of your FUE journey.",
    steps: [
      { num: "01", title: "Consultation & Assessment", desc: "Our specialists assess your hair loss condition, evaluate your donor area, and determine your eligibility for the procedure." },
      { num: "02", title: "Designing the Hairline",     desc: "We design your new hairline considering facial features, age, and natural hairline for an aesthetic, natural outcome." },
      { num: "03", title: "Preparation for Procedure",  desc: "In a sterile, comfortable setting, your scalp is numbed with local anaesthesia for a painless experience." },
      { num: "04", title: "Extraction of Follicles",    desc: "Individual hair follicles are extracted from the donor area using a specialised punch tool, with precision and care." },
      { num: "05", title: "Implantation of Follicles",  desc: "Extracted follicles are carefully implanted into recipient sites, blending seamlessly with your existing hairline." },
    ],
  },

  /* ── BENEFITS ── */
  benefits: {
    eyebrow: "The Benefits",
    heading: "Benefits of FUE Hair Transplant",
    items: [
      { title: "Natural & Permanent",      desc: "Transplanted follicles are resistant to DHT, ensuring hair continues to grow, providing a lasting solution to hair loss." },
      { title: "Minimally Invasive",       desc: "No linear scars or visible signs of surgery, meaning minimal downtime and faster recovery." },
      { title: "Natural-Looking Results",  desc: "Precise extraction and implantation create a hairline that blends seamlessly with existing hair, growing in the same direction." },
      { title: "Versatile for All",        desc: "Suitable for men and women alike, tailored to restore hairlines, fill thinning areas, or enhance density." },
      { title: "Personalised Care",        desc: "Our skilled and experienced team guides you through the entire process, from consultation to aftercare." },
    ],
  },

  /* ── WHY CHOOSE US ── */
  candidates: {
    eyebrow: "Is FUE Right for You?",
    heading: "Why Choose Clinique Internationale for FUE?",
    ideal: [
      "Individuals with early to moderate hair loss and a healthy donor area",
      "Men and women looking to restore a receding hairline or fill thinning areas",
      "Those who want minimal downtime and a faster recovery than traditional methods",
      "Patients seeking a natural, virtually undetectable hairline",
    ],
    notIdeal: [
      "Patients with very advanced hair loss and insufficient donor hair",
      "Individuals with a strong tendency toward keloid or excessive scarring",
      "Those expecting results without committing to the full post-procedure care plan",
    ],
  },

  /* ── FAQs ── */
  faqs: [
    {
      q: "How long does an FUE hair transplant take?",
      a: "Depending on the number of grafts required, a session typically takes between 4 and 8 hours, and may be spread across a single day or two consecutive days for larger cases.",
    },
    {
      q: "Is FUE painful?",
      a: "The scalp is numbed with local anaesthesia before the procedure begins, so patients experience minimal to no discomfort during extraction and implantation.",
    },
    {
      q: "How long is the recovery period?",
      a: "Most patients return to normal daily activities within a few days. Since FUE leaves no linear scar, downtime is significantly shorter than traditional strip-harvesting methods.",
    },
    {
      q: "When will I see the final results?",
      a: "Initial shedding of transplanted hair is normal in the first few weeks. New growth typically begins around month 3–4, with full, mature results visible by 12 months.",
    },
    {
      q: "Is FUE suitable for women?",
      a: "Yes. FUE is effective for both male and female pattern hair loss, and can be tailored to restore density, fill in thinning areas, or refine a hairline for female patients.",
    },
  ],

  /* ── CTA ── */
  cta: {
    eyebrow: "Take the First Step",
    heading: "Begin Your FUE Hair Transplant Journey",
    sub: "Book a personalised consultation with our specialists at Clinique Internationale and get an accurate assessment of your donor area, graft count, and expected results.",
    primary:   { label: "Book Consultation", href: "https://api.whatsapp.com/send?phone=919082123009&text=I%27m%20interested%20in%20FUE%20Hair%20Transplant" },
    secondary: { label: "View All Treatments", href: "/follicular-unit-extraction.php" },
  },
};

/* ============================================================
   REUSABLE SUB-COMPONENTS  (identical across all treatment pages)
============================================================ */
function Eyebrow({ children, light = false, align = "left" }) {
  return (
    <p style={cinzel} className={`text-[10.5px] tracking-[0.28em] uppercase mb-3 flex items-center gap-2.5 ${align === "center" ? "justify-center" : "justify-start"} ${light ? "text-[#c9a84c]/70" : "text-[#a07828]"}`}>
      <span className={`w-6 h-px ${light ? "bg-[#c9a84c]/50" : "bg-[#c9a84c]"}`} />
      {children}
    </p>
  );
}

function SectionHeading({ children, light = false, align = "left" }) {
  return (
    <h2 style={cinzel} className={`font-black text-[1.3rem] sm:text-[1.6rem] md:text-[1.85rem] uppercase tracking-[0.05em] mb-8 leading-tight ${align === "center" ? "text-center" : "text-left"} ${light ? "text-white" : "text-[#1a0f02]"}`}>
      {children}
    </h2>
  );
}

function GoldDivider() {
  return <div className="w-full h-px bg-gradient-to-r from-transparent via-[#c9a84c]/30 to-transparent" />;
}

function FramedImage({ src, alt, className = "" }) {
  return (
    <div className={`relative rounded-2xl overflow-hidden border border-[#c9a84c]/25 ${className}`}>
      <img src={src} alt={alt} className="w-full h-full object-cover" />
      <span className="absolute inset-0 ring-1 ring-inset ring-[#c9a84c]/15 rounded-2xl pointer-events-none" />
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`border rounded-xl overflow-hidden transition-colors duration-300 ${open ? "border-[#c9a84c]/40 bg-[#c9a84c]/[0.04]" : "border-[#c9a84c]/15 bg-white"}`}>
      <button className="w-full flex items-center justify-between gap-4 px-5 sm:px-6 py-5 text-left" onClick={() => setOpen(p => !p)}>
        <span style={cinzel} className="text-[13px] font-bold tracking-[0.04em] text-[#1a0f02]">{q}</span>
        <span className={`flex-shrink-0 w-7 h-7 rounded-full border border-[#c9a84c]/35 flex items-center justify-center transition-transform duration-300 ${open ? "rotate-45 bg-[#c9a84c] border-[#c9a84c]" : ""}`}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M6 2v8M2 6h8" stroke={open ? "#1a0f02" : "#c9a84c"} strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </span>
      </button>
      {open && <div className="px-5 sm:px-6 pb-5"><p className="text-[13.5px] leading-[1.8] text-[#6b5535]">{a}</p></div>}
    </div>
  );
}

/* ============================================================
   PAGE COMPONENT
============================================================ */
export default function FUEHairTransplant() {
  const pageRef     = useRef(null);
  const heroEyeRef  = useRef(null);
  const heroHeadRef = useRef(null);
  const heroSubRef  = useRef(null);
  const secRefs     = useRef([]);
  const addSection  = (el) => el && !secRefs.current.includes(el) && secRefs.current.push(el);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const ease = "power3.out";
      gsap.fromTo(heroEyeRef.current,  { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.7,  ease });
      gsap.fromTo(heroHeadRef.current, { opacity: 0, y: 26 }, { opacity: 1, y: 0, duration: 0.75, ease, delay: 0.1  });
      gsap.fromTo(heroSubRef.current,  { opacity: 0, y: 22 }, { opacity: 1, y: 0, duration: 0.7,  ease, delay: 0.18 });
      secRefs.current.forEach((el) => {
        if (!el) return;
        gsap.fromTo(el, { opacity: 0, y: 36 }, {
          opacity: 1, y: 0, duration: 0.75, ease,
          scrollTrigger: { trigger: el, start: "top 88%", toggleActions: "play none none reverse" },
        });
      });
    }, pageRef);
    return () => ctx.revert();
  }, []);

  const wrap = "w-full px-5 sm:px-10 lg:px-16 xl:px-24";
  const sec  = "py-16 sm:py-20 md:py-24";

  return (
    <>
    <SEO
  title="FUE Hair Transplant | Clinique Internationale"
  description="Advanced Follicular Unit Extraction (FUE) hair transplant at Clinique Internationale, led by Dr. Bhavesh Gupta. Natural, minimally invasive results."
  url={`${SITE_ORIGIN}/follicular-unit-extraction.php`}
/>
      <link href="https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=Raleway:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&display=swap" rel="stylesheet" />

      <div ref={pageRef} className="relative w-full bg-[#f2ece0] overflow-hidden mt-16 lg:mt-20" style={{ fontFamily: "'Raleway', sans-serif" }}>

  {/* ══════════════════════════════════════
      HERO
  ══════════════════════════════════════ */}
  <div className="relative w-full h-[200px] xs:h-[280px] sm:h-[340px] md:h-[700px] flex items-center justify-end overflow-hidden">
    <img className="absolute inset-0 w-full h-full object-cover object-[center_20%] md:object-center" src={HeroImg} alt={PAGE.hero.heading} />
    <div className="absolute inset-0" />
    <div className="absolute inset-0 opacity-40 pointer-events-none"
      style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E\")" }}
    />
    <div className="relative z-10 text-right px-4 sm:px-10 md:px-16 max-w-[85%] sm:max-w-xl ml-auto mr-0">
      <p ref={heroEyeRef} style={cinzel} className="text-[8px] xs:text-[9px] sm:text-[10.5px] tracking-[0.2em] xs:tracking-[0.24em] sm:tracking-[0.32em] uppercase text-black mb-2 sm:mb-4 flex items-center justify-end gap-2 sm:gap-3">
        <span className="w-4 sm:w-7 h-px bg-black" />{PAGE.hero.eyebrow}<span className="w-4 sm:w-7 h-px bg-black" />
      </p>
      <h1 ref={heroHeadRef} style={cinzel} className="font-black text-[1.4rem] xs:text-[1.8rem] sm:text-[2.8rem] md:text-[3.4rem] tracking-[0.02em] sm:tracking-[0.04em] uppercase text-black mb-2 sm:mb-3 leading-[1.15] sm:leading-tight">
        {PAGE.hero.heading}
      </h1>
      <p ref={heroSubRef} className="text-[11px] xs:text-[12px] sm:text-[15px] text-black/70 leading-relaxed ml-auto">{PAGE.hero.sub}</p>
    </div>
  </div>

        {/* ── BREADCRUMB ── */}
        <div className="relative w-full overflow-hidden">
          <img className="absolute inset-0 w-full h-full object-cover" src={BreadcrumbImg} alt="" aria-hidden="true" />
          <div className="absolute inset-0 bg-[#0e0905]/88" />
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/25 to-transparent" />
          <div className={`relative ${wrap} py-3.5 flex flex-wrap items-center gap-2`}>
            {PAGE.breadcrumb.map((b, i) => (
              <span key={i} className="flex items-center gap-2">
                {i > 0 && <span className="text-[#c9a84c]/30 text-xs">›</span>}
                {b.href
                  ? <a href={b.href} style={cinzel} className="text-[10px] tracking-widest uppercase text-[#c9a84c]/60 hover:text-[#c9a84c] transition-colors">{b.label}</a>
                  : <span style={cinzel} className="text-[10px] tracking-widest uppercase text-[#c9a84c]">{b.label}</span>}
              </span>
            ))}
          </div>
        </div>

        {/* ══════════════════════════════════════
            INTRO
        ══════════════════════════════════════ */}
        <div className={`${wrap} ${sec}`}>
          <div ref={addSection} className="max-w-4xl mx-auto text-center">
            <Eyebrow align="center">{PAGE.intro.eyebrow}</Eyebrow>
            <SectionHeading align="center">{PAGE.intro.heading}</SectionHeading>
            {PAGE.intro.paragraphs.map((p, i) => (
              <p key={i} className="text-[14.5px] leading-[1.9] text-[#3a2e1e] mb-4">{p}</p>
            ))}
          </div>
        </div>

        {/* ══════════════════════════════════════
            THE FUE ADVANTAGE (dark band)
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div className="bg-[#0e0905] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection}>
              <Eyebrow light align="center">{PAGE.science.eyebrow}</Eyebrow>
              <SectionHeading light align="center">{PAGE.science.heading}</SectionHeading>
              <p className="text-[14px] leading-[1.85] text-white/55 max-w-3xl mx-auto text-center mb-12">{PAGE.science.intro}</p>

              <p style={cinzel} className="text-[10px] tracking-[0.22em] uppercase text-[#c9a84c]/60 text-center mb-5">What Sets FUE Apart</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-14">
                {PAGE.science.growthFactors.map((g) => (
                  <div key={g.code} className="relative overflow-hidden bg-white/[0.04] border border-[#c9a84c]/15 rounded-2xl p-5 hover:bg-[#c9a84c]/[0.08] hover:border-[#c9a84c]/30 transition-colors text-center">
                    <span className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/35 to-transparent" />
                    <div style={cinzel} className="font-black text-[1.5rem] leading-none mb-1 bg-gradient-to-br from-[#c9943a] via-[#ffe9a0] to-[#c9943a] bg-clip-text text-transparent">{g.code}</div>
                    <div style={cinzel} className="text-[9px] tracking-[0.1em] uppercase text-[#c9a84c]/70 mb-3 leading-snug">{g.name}</div>
                    <p className="text-[11.5px] leading-[1.7] text-white/50">{g.desc}</p>
                  </div>
                ))}
              </div>

              <p style={cinzel} className="text-[10px] tracking-[0.22em] uppercase text-[#c9a84c]/60 text-center mb-5">Why It Delivers Better Results</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                {PAGE.science.mechanisms.map((m) => (
                  <div key={m.num} className="relative overflow-hidden bg-gradient-to-br from-[#c9a84c]/[0.07] to-[#c9a84c]/[0.02] border border-[#c9a84c]/18 rounded-2xl p-6 hover:border-[#c9a84c]/35 transition-colors">
                    <span className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/35 to-transparent" />
                    <span style={cinzel} className="font-black text-[2rem] leading-none text-[#c9a84c]/18 select-none block mb-3">{m.num}</span>
                    <div style={cinzel} className="text-[12px] font-bold tracking-[0.06em] uppercase text-white/85 mb-2">{m.title}</div>
                    <p className="text-[12.5px] leading-[1.75] text-white/50">{m.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <GoldDivider />

        {/* ══════════════════════════════════════
            SURGEON
        ══════════════════════════════════════ */}
        <div className={`${wrap} ${sec}`}>
          <div ref={addSection} className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            <FramedImage src={SurgeonImg} alt="Dr. Bhavesh Gupta performing FUE hair transplant" className="w-full h-auto object-contain" />
            <div>
              <Eyebrow>{PAGE.surgeon.eyebrow}</Eyebrow>
              <SectionHeading>{PAGE.surgeon.heading}</SectionHeading>
              {PAGE.surgeon.paragraphs.map((p, i) => (
                <p key={i} className="text-[14px] leading-[1.9] text-[#3a2e1e] mb-4">{p}</p>
              ))}
              <div className="flex flex-wrap gap-2 mt-5">
                {PAGE.surgeon.tags.map(t => (
                  <span key={t} style={cinzel} className="text-[9.5px] tracking-[0.12em] uppercase text-[#a07828] border border-[#c9a84c]/30 rounded px-3 py-1">{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            COST + CITY CARDS
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div className="bg-[#faf8f4] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection}>
              <Eyebrow align="center">{PAGE.cost.eyebrow}</Eyebrow>
              <SectionHeading align="center">{PAGE.cost.heading}</SectionHeading>
              {PAGE.cost.paragraphs.map((p, i) => (
                <p key={i} className="text-[14.5px] leading-[1.9] text-[#3a2e1e] max-w-3xl mx-auto text-center mb-4">{p}</p>
              ))}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-4xl mx-auto mt-8">
                {PAGE.cost.cities.map((c, i) => (
                  <a key={i} href={c.href}
                    className="bg-white border border-[#c9a84c]/18 rounded-2xl p-6 relative overflow-hidden hover:-translate-y-1.5 hover:shadow-[0_12px_32px_rgba(90,60,10,0.1)] transition-all duration-300 block text-center group">
                    <span className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent" />
                    <div style={cinzel} className="text-[13px] font-bold tracking-[0.06em] uppercase text-[#1a0f02] mb-1 group-hover:text-[#a07828] transition-colors">{c.city}</div>
                    <div className="text-[11.5px] text-[#6b5535] mb-3">{c.surgeon}</div>
                    <div style={cinzel} className="font-black text-[1rem] leading-snug bg-gradient-to-br from-[#c9943a] via-[#ffe9a0] to-[#c9943a] bg-clip-text text-transparent">{c.range}</div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
        <GoldDivider />

        {/* ══════════════════════════════════════
            PROCEDURE STEPS (image + 2-col grid)
        ══════════════════════════════════════ */}
        <div className="bg-[#0e0905] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection}>
              <Eyebrow light align="center">{PAGE.procedure.eyebrow}</Eyebrow>
              <SectionHeading light align="center">{PAGE.procedure.heading}</SectionHeading>
              <p className="text-[14px] leading-[1.9] text-white/55 max-w-3xl mx-auto text-center mb-10">{PAGE.procedure.intro}</p>
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-10 items-start">
                <FramedImage src={ProcedureImg} alt="FUE follicle extraction and implantation" className="h-[260px] sm:h-[340px] lg:h-full lg:sticky lg:top-24" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {PAGE.procedure.steps.map((s) => (
                    <div key={s.num} className="relative overflow-hidden bg-white/[0.04] border border-[#c9a84c]/15 rounded-2xl p-5 hover:bg-[#c9a84c]/[0.07] hover:border-[#c9a84c]/30 transition-colors">
                      <span className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/35 to-transparent" />
                      <div className="flex items-center gap-2.5 mb-2.5">
                        <span style={cinzel} className="font-black text-[1.3rem] leading-none text-[#c9a84c]/25 select-none flex-shrink-0">{s.num}</span>
                        <div style={cinzel} className="text-[11.5px] font-bold tracking-[0.05em] uppercase text-white/90">{s.title}</div>
                      </div>
                      <p className="text-[12.5px] leading-[1.75] text-white/55">{s.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <GoldDivider />

        {/* ══════════════════════════════════════
            BENEFITS (result image + cards)
        ══════════════════════════════════════ */}
        <div className={`${wrap} ${sec}`}>
          <div ref={addSection}>
            <Eyebrow align="center">{PAGE.benefits.eyebrow}</Eyebrow>
            <SectionHeading align="center">{PAGE.benefits.heading}</SectionHeading>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.15fr] gap-10 items-center">
              <FramedImage src={ResultImg} alt="Natural results after FUE hair transplant" className="h-[260px] sm:h-[320px]" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {PAGE.benefits.items.map((b, i) => (
                  <div key={i} className="bg-white border border-[#c9a84c]/18 rounded-2xl p-5 relative overflow-hidden hover:-translate-y-1 hover:shadow-[0_8px_28px_rgba(90,60,10,0.08)] transition-all duration-300">
                    <span className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent" />
                    <div className="flex items-center gap-2.5 mb-2">
                      <span style={cinzel} className="text-[11px] font-black text-[#c9a84c]">{String(i + 1).padStart(2, "0")}</span>
                      <div style={cinzel} className="text-[11.5px] font-bold tracking-[0.05em] uppercase text-[#1a0f02]">{b.title}</div>
                    </div>
                    <p className="text-[12.5px] leading-[1.75] text-[#6b5535]">{b.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            WHY CHOOSE US (dark band)
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div className="bg-[#0e0905] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection}>
              <Eyebrow light align="center">{PAGE.candidates.eyebrow}</Eyebrow>
              <SectionHeading light align="center">{PAGE.candidates.heading}</SectionHeading>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">

                {/* Ideal candidates */}
                <div className="relative overflow-hidden bg-gradient-to-br from-[#c9a84c]/[0.07] to-[#c9a84c]/[0.02] border border-[#c9a84c]/18 rounded-2xl p-7">
                  <span className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent" />
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-8 h-8 rounded-full bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center flex-shrink-0">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7l4 4 6-6" stroke="#4ade80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </div>
                    <span style={cinzel} className="text-[12px] font-bold uppercase tracking-[0.06em] text-white/85">Great Fit For</span>
                  </div>
                  <ul className="flex flex-col gap-3">
                    {PAGE.candidates.ideal.map((item, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-[13px] leading-[1.7] text-white/60">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#c9a84c] flex-shrink-0 mt-1.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Not ideal */}
                <div className="relative overflow-hidden bg-white/[0.03] border border-white/10 rounded-2xl p-7">
                  <span className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-8 h-8 rounded-full bg-amber-500/15 border border-amber-400/30 flex items-center justify-center flex-shrink-0">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 3v5M7 10v1" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round"/></svg>
                    </div>
                    <span style={cinzel} className="text-[12px] font-bold uppercase tracking-[0.06em] text-white/85">May Need a Different Approach</span>
                  </div>
                  <ul className="flex flex-col gap-3">
                    {PAGE.candidates.notIdeal.map((item, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-[13px] leading-[1.7] text-white/55">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400/60 flex-shrink-0 mt-1.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

              </div>
            </div>
          </div>
        </div>
        <GoldDivider />

        {/* ══════════════════════════════════════
            FAQs
        ══════════════════════════════════════ */}
        <div className="bg-[#faf8f4] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection}>
              <Eyebrow align="center">Common Questions</Eyebrow>
              <SectionHeading align="center">Frequently Asked Questions</SectionHeading>
              <div className="max-w-3xl mx-auto flex flex-col gap-3">
                {PAGE.faqs.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} />)}
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            CTA
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div className={`${wrap} ${sec}`}>
          <div ref={addSection} className="max-w-xl mx-auto text-center">
            <Eyebrow align="center">{PAGE.cta.eyebrow}</Eyebrow>
            <h3 style={cinzel} className="font-black text-[1.3rem] sm:text-[1.65rem] md:text-[1.9rem] text-[#1a0f02] uppercase tracking-[0.06em] mb-3.5">{PAGE.cta.heading}</h3>
            <p className="text-[14px] text-[#6b5535] leading-[1.75] mb-8">{PAGE.cta.sub}</p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <a href={PAGE.cta.primary.href} target="_blank" rel="noopener noreferrer"
                style={{ ...cinzel, backgroundImage: "linear-gradient(135deg,#c9943a,#ffe9a0,#c9943a)", backgroundSize: "200% 100%" }}
                className="text-[11px] font-bold tracking-[0.16em] uppercase px-8 py-3.5 rounded-md text-[#1a0f02] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(201,168,76,0.35)] transition-all inline-block">
                {PAGE.cta.primary.label}
              </a>
              <a href={PAGE.cta.secondary.href} style={cinzel}
                className="text-[11px] font-bold tracking-[0.16em] uppercase px-8 py-[13px] rounded-md bg-transparent border border-[#1a0f02]/35 text-[#1a0f02] hover:-translate-y-0.5 hover:border-[#c9a84c] hover:text-[#a07828] transition-all inline-block">
                {PAGE.cta.secondary.label}
              </a>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}